import type { ModuleId, PermissionLevel, RequiredLevel } from '@qlk/shared';
import type { ComponentType, LazyExoticComponent } from 'react';

import { coQuyen } from '../../lib/auth.js';
import type { KyTrang } from '../../lib/kyTrang.js';

/**
 * Điểm mở rộng của trang chi tiết kênh.
 *
 * Module khác thêm **mục** bằng cách tạo file
 * `apps/web/src/modules/<id>/channelTabs.tsx` export mảng `TabKenh`
 * (`export default [...]` hoặc `export const channelTabs = [...]`).
 * Không phải sửa code của `core` hay `channels`.
 *
 * ## Bước 2/3 của nâng cấp giao diện: 9 tab → 5 tab
 *
 * Trước đây mỗi module có **một tab riêng**, nên trang kênh có chín tab và
 * người dùng phải nhớ "tỷ lệ chia nằm ở tab nào trong ba tab nói về tiền".
 * Nay mỗi mục khai `nhom` của mình; trang chỉ hiện **năm tab** và xếp các mục
 * của cùng một nhóm vào một trang.
 *
 * Mục nào thuộc nhóm `doanh-thu-chia-se` còn khai `cot`: nhóm đó bày hai cột
 * (xem `TrangChiTietKenh`), cột phải là phần "cách chia" đọc theo chiều dọc.
 *
 * ```tsx
 * const channelTabs: TabKenh[] = [
 *   {
 *     id: 'doanh-thu',
 *     nhom: 'doanh-thu-chia-se',
 *     cot: 'trai',
 *     label: 'Doanh thu theo kỳ',
 *     order: 30,
 *     permission: { module: 'revenue', level: 'view' },
 *     component: lazy(() => import('./components/TabDoanhThu.js')),
 *   },
 * ];
 * export default channelTabs;
 * ```
 */

export const NHOM_TAB_KENH = [
  'tong-quan',
  'doanh-thu-chia-se',
  'noi-dung-nhac',
  'ban-quyen',
  'cai-dat',
] as const;

export type NhomTabKenh = (typeof NHOM_TAB_KENH)[number];

export const NHAN_NHOM_TAB: Record<NhomTabKenh, string> = {
  'tong-quan': 'Tổng quan',
  'doanh-thu-chia-se': 'Doanh thu & chia sẻ',
  'noi-dung-nhac': 'Nội dung & nhạc',
  'ban-quyen': 'Bản quyền',
  'cai-dat': 'Cài đặt kênh',
};

/**
 * Id tab **cũ** → nhóm mới.
 *
 * Link và dấu trang cũ (`/kenh/<id>?tab=dong-bo`) phải còn mở được: trang đổi
 * `?tab=` sang nhóm mới rồi `replace` URL, kèm `#muc-dong-bo` để nhảy đúng chỗ
 * trong trang mới — xem `TrangChiTietKenh`.
 */
export const TAB_CU_SANG_NHOM: Record<string, NhomTabKenh> = {
  'hieu-suat': 'tong-quan',
  network: 'doanh-thu-chia-se',
  'doanh-thu': 'doanh-thu-chia-se',
  'doi-ngu': 'doanh-thu-chia-se',
  nhac: 'noi-dung-nhac',
  'noi-dung': 'noi-dung-nhac',
  'ban-quyen': 'ban-quyen',
  'ho-so': 'cai-dat',
  'phan-cong': 'cai-dat',
  'dong-bo': 'cai-dat',
};

/**
 * Id **tab con cũ** của khối "Số liệu phân tích về kênh" → mục trong tab mới.
 *
 * Bước 3 (22/10/2026) bỏ ba tab con (Tổng quan · Đối tượng người xem · Doanh
 * thu) và dàn nội dung ra ba tab chính. Tab con không nằm trên URL nên không
 * có dấu trang nào để hỏng, nhưng link dạng `?phanTich=doanh-thu` có trong tài
 * liệu nội bộ, nên trang vẫn nhận và chuyển đúng chỗ.
 */
export const PHAN_TICH_CU_SANG_MUC: Record<string, { nhom: NhomTabKenh; muc: string }> = {
  'tong-quan': { nhom: 'tong-quan', muc: 'hieu-suat' },
  'doi-tuong': { nhom: 'tong-quan', muc: 'doi-tuong' },
  'doanh-thu': { nhom: 'doanh-thu-chia-se', muc: 'hieu-suat-doanh-thu' },
  'noi-dung': { nhom: 'noi-dung-nhac', muc: 'noi-dung' },
};

/**
 * Props mọi mục nhận được.
 *
 * - `ky` là **kỳ của cả trang** (`?ky=28d|90d|YYYY-MM`): mục nào nói về số
 *   liệu theo ngày thì dùng `ky.from` / `ky.to`.
 * - `thang` là kỳ tiền `YYYY-MM` của cùng kỳ đó (`ky.thang`): mục theo tháng
 *   (sao kê, hợp đồng, chia sẻ, chi phí) dùng nó và **không** phải biết người
 *   dùng đang chọn kỳ lăn hay tháng — trang tự treo dải "các khối dưới đây
 *   tính theo tháng N" khi hai thứ không trùng nhau.
 */
export interface ChannelTabProps {
  channelId: string;
  thang: string;
  ky: KyTrang;
}

export interface TabKenh {
  /**
   * Id của mục — **giữ nguyên id tab cũ**: nó là khoá của neo `#muc-<id>` và
   * của bảng chuyển hướng `TAB_CU_SANG_NHOM`.
   */
  id: string;
  /** Tab mà mục này nằm trong. */
  nhom: NhomTabKenh;
  /** Nhãn của mục, in làm tiêu đề khối khi nhóm có nhiều mục. */
  label: string;
  /** Nhỏ hơn đứng trước, tính trong phạm vi một nhóm. */
  order: number;
  /**
   * Cột của mục trong nhóm hai cột. Mặc định `trai`.
   *
   * `dai` = **chiếm trọn bề ngang**, đặt trên lưới hai cột. Dành cho dải chỉ số
   * của tab: bốn ô tiền nhồi vào cột trái 1,55fr thì nhãn xuống dòng từng từ ở
   * laptop 1280px, mà dải chỉ số là thứ người ta mở tab để xem đầu tiên.
   */
  cot?: 'trai' | 'phai' | 'dai';
  /** Quyền tối thiểu để thấy mục. Thiếu quyền thì mục bị ẩn hẳn. */
  permission: { module: ModuleId; level: RequiredLevel };
  /** Luôn lazy-load: chỉ tải bundle khi người dùng mở tab. */
  component: LazyExoticComponent<ComponentType<ChannelTabProps>>;
}

type ModuleTab = { default?: TabKenh[]; channelTabs?: TabKenh[] };

const DA_NAP = import.meta.glob<ModuleTab>('../*/channelTabs.{ts,tsx}', { eager: true });

/** Mọi mục đã đăng ký, đã sắp theo `order`. */
export function tatCaTabKenh(): TabKenh[] {
  return Object.values(DA_NAP)
    .flatMap((m) => m.default ?? m.channelTabs ?? [])
    .sort((a, b) => a.order - b.order);
}

/** Mục mà người dùng hiện tại được xem. */
export function tabKenhTheoQuyen(
  permissions: Record<string, PermissionLevel> | undefined,
): TabKenh[] {
  return tatCaTabKenh().filter((t) =>
    coQuyen(permissions, t.permission.module, t.permission.level),
  );
}

export interface NhomDaDung {
  nhom: NhomTabKenh;
  label: string;
  muc: TabKenh[];
  /** Mục trọn bề ngang, trên lưới hai cột. */
  dai: TabKenh[];
  trai: TabKenh[];
  phai: TabKenh[];
}

/**
 * Năm nhóm, đã lọc theo quyền. **Nhóm không còn mục nào thì không hiện** — một
 * tab mở ra trống rỗng tệ hơn một tab không có.
 */
export function nhomTabTheoQuyen(
  permissions: Record<string, PermissionLevel> | undefined,
): NhomDaDung[] {
  const duoc = tabKenhTheoQuyen(permissions);

  return NHOM_TAB_KENH.map((nhom) => {
    const muc = duoc.filter((t) => t.nhom === nhom);
    return {
      nhom,
      label: NHAN_NHOM_TAB[nhom],
      muc,
      dai: muc.filter((t) => t.cot === 'dai'),
      trai: muc.filter((t) => (t.cot ?? 'trai') === 'trai'),
      phai: muc.filter((t) => t.cot === 'phai'),
    };
  }).filter((n) => n.muc.length > 0);
}
